import { CommonModule } from '@angular/common';
import {
  NgbNav,
  NgbNavItem,
  NgbNavItemRole,
  NgbNavLink,
  NgbNavLinkBase,
  NgbNavContent,
  NgbScrollSpy,
  NgbScrollSpyFragment,
  NgbNavOutlet,
} from '@ng-bootstrap/ng-bootstrap';
import { Component, computed, Injector, OnInit, signal, Signal, ViewChild } from '@angular/core';
import { ReportViewerComponent } from '@odyssey/reporting';
import {
  CreateUpdateCustomerPreparationDto,
  CustomerDocDefaultValueDto,
  CustomerPreparationDto,
  CustomerPreparationService,
  CustomerPreparationStatus,
  DayBookService,
} from '@digiwall/goliath-documents/proxy';
import { ClassificationTypeService, DocRole, LabelSubContext } from '@digiwall/goliath-core/proxy';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ConfigStateService,
  LocalizationService,
  SubscriptionService,
  LocalizationModule,
} from '@abp/ng.core';
import { FormBuilder, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { BreadcrumbService } from 'xng-breadcrumb';
import { CustomerService, PointOfSaleService } from '@digiwall/goliath-contacts/proxy';
import { DocTemplateService } from '../../base-docs/detail/doc-template-selection/doc-template.service';
import { BaseCustomerDocDetail } from '../../base-customer-docs/base-customer-doc-detail';
import { FieldMapping } from '../../base-third-party-docs/base-third-party-doc-detail';
import {
  ChatComponent,
  DeactivatableComponentService,
  HistoryComponent,
  DwKeyboardShortcutDirective,
  DwNavLinkDirective,
} from '@digiwall/core';
import { AnnexesListComponent } from '@digiwall/goliath-core';
import {
  CounterComponent,
  DialogBoxComponent,
  DropdownMenu,
  DropdownMenuComponent,
  PanelComponent,
  SidePanelComponent,
} from '@digiwall/ui-kit';
import { DocReportPermission } from '@digiwall/goliath-documents/config';
import {
  DwGridDetailNavigationComponent,
  DwScrollSpyDirective,
  DwTabsComponent,
} from '@digiwall/ag-grid';
import { NgxValidateCoreModule } from '@ngx-validate/core';
import { DocConditionsComponent } from '../../base-docs/detail/doc-conditions/doc-conditions.component';
import { DocClassificationsComponent } from '../../base-docs/detail/doc-classifications/doc-classifications.component';
import { DocCustomerAddressesComponent } from '../../base-docs/detail/doc-customer-addresses/doc-customer-addresses.component';
import { DocCustomerOthersComponent } from '../../base-docs/detail/doc-customer-others/doc-customer-others.component';
import { AsyncPipe } from '@angular/common';
import { DocSidePanelComponent } from '../../base-docs/detail/doc-ui-components/doc-side-panel/doc-side-panel.component';
import {
  DocumentCoreCardComponent,
  DocumentCustomerCardComponent,
  DocumentCustomerDateCardComponent,
  DocumentCustomerStatusCardComponent,
} from '../../../components';
import { CustomerPreparationLinesEditorComponent } from './customer-preparation-lines/customer-preparation-lines-editor.component';
import { LabelPrinterHelper } from '@digiwall/goliath-products';

@Component({
  selector: 'document-customer-preparation-detail',
  templateUrl: './customer-preparation-detail.component.html',
  styleUrl: './customer-preparation-detail.component.scss',
  providers: [DocTemplateService],
  imports: [
    CommonModule,
    PanelComponent,
    DwTabsComponent,
    NgbNav,
    NgbNavItem,
    NgbNavItemRole,
    NgbNavLink,
    NgbNavLinkBase,
    DwNavLinkDirective,
    NgbNavContent,
    NgbScrollSpy,
    ReactiveFormsModule,
    NgxValidateCoreModule,
    DwKeyboardShortcutDirective,
    NgbScrollSpyFragment,
    DwScrollSpyDirective,
    CustomerPreparationLinesEditorComponent,
    DocConditionsComponent,
    DocClassificationsComponent,
    DocCustomerAddressesComponent,
    DocCustomerOthersComponent,
    NgbNavOutlet,
    SidePanelComponent,
    FormsModule,
    AsyncPipe,
    LocalizationModule,
    CounterComponent,
    ChatComponent,
    DwGridDetailNavigationComponent,
    DocSidePanelComponent,
    DocumentCoreCardComponent,
    DocumentCustomerCardComponent,
    DocumentCustomerDateCardComponent,
    DocumentCustomerStatusCardComponent,
    HistoryComponent,
    DropdownMenuComponent,
    ReportViewerComponent,
    DialogBoxComponent,
    AnnexesListComponent,
  ],
})
export class CustomerPreparationDetailComponent
  extends BaseCustomerDocDetail<
    CustomerPreparationService,
    CustomerPreparationDto,
    CreateUpdateCustomerPreparationDto,
    CustomerDocDefaultValueDto
  >
  implements OnInit
{
  @ViewChild('customerPreparationLineGrid') docLineGrid!: CustomerPreparationLinesEditorComponent;
  @ViewChild('report') reportViewer!: ReportViewerComponent;

  docRole = DocRole.CustomerPreparation;

  protected readonly isReadonly = signal(true);
  protected saveActionBtns = computed(() => []);
  protected actions: Signal<DropdownMenu> = computed(() => ({
    groups: [
      [
        {
          label: '::GeneratePdf',
          icon: 'digi-out-pdf-file',
          action: () => this.generatePdf(this.reportViewer),
        },
        {
          label: 'Doc::PrintDocument',
          icon: 'digi-out-print',
          action: () => this.printDocument(),
          enabled: this.canPrintDocument,
        },
        {
          label: 'Doc::PrintLabel',
          icon: 'digi-out-print',
          action: () => this.printLabel(),
        },
      ],
      [
        {
          label: 'Doc::GenerateDocPrintTemplate',
          icon: 'digi-out-edit-square',
          action: () => this.openDesigner(),
          requiredPolicy: DocReportPermission.Create,
        },
        {
          label: 'Doc::GenerateLabelPrintTemplate',
          icon: 'digi-out-edit-square',
          action: () =>
            this.labelPrinterHelper.openDesigner(
              this.entity.id,
              LabelSubContext.CustomerDocOrderInfo,
            ),
        },
      ],
      [
        {
          label: 'Doc::CustomerPreparation:StartPreparation',
          icon: 'digi-out-play',
          action: () => this.startPreparation(),
          enabled: computed(() => {
            const doc = this.entitySignal();
            return doc != null && doc.preparationStatus === CustomerPreparationStatus.ToDo;
          }),
        },
        {
          label: 'Doc::CustomerPreparation:CompletePreparation',
          icon: 'digi-out-checklist',
          action: () => this.completePreparation(),
          enabled: computed(() => {
            const doc = this.entitySignal();
            return doc != null && doc.preparationStatus === CustomerPreparationStatus.InProgress;
          }),
        },
        {
          label: 'Doc::CustomerPreparation:CancelPreparation',
          icon: 'digi-out-arrow-back-circle',
          action: () => this.cancelPreparation(),
          enabled: computed(() => {
            const doc = this.entitySignal();
            return (
              doc != null &&
              (doc.preparationStatus === CustomerPreparationStatus.ToDo ||
                doc.preparationStatus === CustomerPreparationStatus.InProgress)
            );
          }),
        },
      ],
    ],
  }));

  override get defaultValue() {
    return Object.assign(this.baseDefaultValue, {
      isExported: false,
      allowPartialDelivery: false,
    });
  }

  constructor(
    route: ActivatedRoute,
    router: Router,
    subscriptionService: SubscriptionService,
    customerPreparationService: CustomerPreparationService,
    formBuilder: FormBuilder,
    toastService: ToasterService,
    breadcrumbService: BreadcrumbService,
    dayBookService: DayBookService,
    localizationService: LocalizationService,
    injector: Injector,
    customerService: CustomerService,
    config: ConfigStateService,
    docTemplateService: DocTemplateService,
    classificationTypeService: ClassificationTypeService,
    protected pointOfSaleService: PointOfSaleService,
    confirmationService: ConfirmationService,
    deactivableComponentService: DeactivatableComponentService,
    protected labelPrinterHelper: LabelPrinterHelper,
  ) {
    super(
      route,
      router,
      subscriptionService,
      customerPreparationService,
      formBuilder,
      toastService,
      breadcrumbService,
      dayBookService,
      localizationService,
      classificationTypeService,
      docTemplateService,
      injector,
      customerService,
      config,
      confirmationService,
      deactivableComponentService,
      labelPrinterHelper,
    );
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.init();
  }
  protected override disableForm(): void {
    super.disableForm();
    this.form.get('documentDate')?.enable();
  }
  override get formModel() {
    return Object.assign({}, super.formModel, {
      invoicingAddress_ThirdPartyAddressId: [null],
      deliveryDate: [null],
    });
  }

  protected override onThirdPartyChanged = (thirdPartyId: string): void => {
    super.onThirdPartyChanged(thirdPartyId);
    this.thirdPartyId$.next(thirdPartyId);
  };

  protected override getFieldMappings(): FieldMapping[] {
    return [
      ...super.getFieldMappings(),
      { formField: 'representativeCommision', customerField: 'representativeCommission' },
    ];
  }

  protected override getEntityCallback(): void {
    super.getEntityCallback();
    super.loadThirdParties(this.entity?.isClosed ?? true);
    this.form.markAsPristine();
    this.disableForm();
  }

  private startPreparation(): void {
    this.docService.startPreparation(this.docId).subscribeOnce(() => this.getEntity(this.docId));
  }

  private cancelPreparation(): void {
    this.confirmationService
      .warn('Doc::CustomerPreparation:CancelPreparationConfirmation', '::AreYouSure')
      .subscribeOnce(status => {
        if (status !== Confirmation.Status.confirm) return;
        this.docService
          .cancelPreparation(this.docId)
          .subscribeOnce(() => this.getEntity(this.docId));
      });
  }

  private completePreparation(): void {
    this.confirmationService
      .warn('Doc::CustomerPreparation:CompletePreparationConfirmation', '::AreYouSure')
      .subscribeOnce(status => {
        if (status !== Confirmation.Status.confirm) return;
        this.docService
          .completePreparation(this.docId)
          .subscribeOnce(() => this.getEntity(this.docId));
      });
  }

  public override canDeactivate = async () => true;
}
